/**
 * Compliance Reports Page
 * Epic #17: FERPA/COPPA/GDPR Compliance Reporting
 *
 * Comprehensive compliance reporting dashboard with PDF export
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Column,
  ContentSwitcher,
  Switch,
  Tile,
  DatePicker,
  DatePickerInput,
  Button,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  ProgressBar,
  Tag,
  Loading,
  InlineNotification,
  SkeletonText,
} from '@carbon/react';
import { Download, DocumentPdf, Report, Security, CheckmarkFilled, WarningFilled } from '@carbon/icons-react';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';
import { useTheme } from '../../contexts/ThemeContext';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface ComplianceMetrics {
  total_accesses?: number;
  authorized_accesses?: number;
  violations?: number;
  compliance_rate?: number;
  total_children?: number;
  compliant_children?: number;
  non_compliant_children?: number;
  total_users?: number;
  verified_users?: number;
  users_with_audit_trail?: number;
  verification_rate?: number;
  audit_trail_coverage?: number;
}

interface ComplianceReport {
  framework: string;
  report_period: {
    start_date: string;
    end_date: string;
  };
  compliance_score: number;
  metrics: ComplianceMetrics;
  violations?: any[];
  non_compliant_users?: any[];
  authorized_access_by_role?: any[];
  data_subject_rights?: any;
  recommendations: string[];
}

const ComplianceReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { theme } = useTheme();

  // Get token from localStorage directly
  const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || '';

  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Date range state
  const [startDate, setStartDate] = useState<Date>(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
  const [endDate, setEndDate] = useState<Date>(new Date());

  // Report data state
  const [ferpaReport, setFerpaReport] = useState<ComplianceReport | null>(null);
  const [coppaReport, setCoppaReport] = useState<ComplianceReport | null>(null);
  const [gdprReport, setGdprReport] = useState<ComplianceReport | null>(null);

  // Export state
  const [exporting, setExporting] = useState(false);

  // Format date for API
  const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  // Fetch FERPA report
  const fetchFerpaReport = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_BASE_URL}/v1/admin/rbac/compliance/ferpa`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          start_date: formatDate(startDate),
          end_date: formatDate(endDate),
        },
      });

      if (response.data.success) {
        setFerpaReport(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load FERPA report');
      }
    } catch (err: any) {
      console.error('Failed to fetch FERPA report:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load FERPA report');
    } finally {
      setLoading(false);
    }
  };

  // Fetch COPPA report
  const fetchCoppaReport = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_BASE_URL}/v1/admin/rbac/compliance/coppa`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          start_date: formatDate(startDate),
          end_date: formatDate(endDate),
        },
      });

      if (response.data.success) {
        setCoppaReport(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load COPPA report');
      }
    } catch (err: any) {
      console.error('Failed to fetch COPPA report:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load COPPA report');
    } finally {
      setLoading(false);
    }
  };

  // Fetch GDPR report
  const fetchGdprReport = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_BASE_URL}/v1/admin/rbac/compliance/gdpr`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          start_date: formatDate(startDate),
          end_date: formatDate(endDate),
        },
      });

      if (response.data.success) {
        setGdprReport(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load GDPR report');
      }
    } catch (err: any) {
      console.error('Failed to fetch GDPR report:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load GDPR report');
    } finally {
      setLoading(false);
    }
  };

  // Generate and download PDF report
  const exportToPdf = async (framework: 'FERPA' | 'COPPA' | 'GDPR') => {
    try {
      setExporting(true);
      setError(null);
      setSuccess(null);

      const response = await axios.post(
        `${API_BASE_URL}/v1/admin/rbac/compliance/export-pdf`,
        {
          framework,
          start_date: formatDate(startDate),
          end_date: formatDate(endDate),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        const downloadUrl = `${API_BASE_URL}${response.data.data.download_url}`;

        // Open in new window for download
        window.open(downloadUrl, '_blank');

        setSuccess(`${framework} compliance report exported successfully`);
      } else {
        setError(response.data.message || 'Failed to export report');
      }
    } catch (err: any) {
      console.error('Failed to export report:', err);
      setError(err.response?.data?.message || err.message || 'Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  // BUGFIX: Do NOT auto-fetch on mount - users should click "Generate Report" button
  // Auto-fetching causes the component to stuck in loading state if API fails
  // Previous code was calling fetch functions immediately which set loading=true
  // If API call failed or timed out, loading state never cleared, preventing tabs from rendering
  //
  // useEffect(() => {
  //   if (selectedTab === 0) {
  //     fetchFerpaReport();
  //   } else if (selectedTab === 1) {
  //     fetchCoppaReport();
  //   } else if (selectedTab === 2) {
  //     fetchGdprReport();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [selectedTab]);

  // Render compliance score tile
  const renderComplianceScore = (report: ComplianceReport | null) => {
    if (!report) return null;

    const scoreColor = report.compliance_score >= 95 ? 'green' : report.compliance_score >= 80 ? 'purple' : 'red';

    return (
      <Tile style={{ padding: '2rem', textAlign: 'center', marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
          {report.compliance_score >= 95 ? (
            <CheckmarkFilled size={32} style={{ color: 'var(--cds-support-success)', marginRight: '0.5rem' }} />
          ) : (
            <WarningFilled size={32} style={{ color: 'var(--cds-support-error)', marginRight: '0.5rem' }} />
          )}
          <h2 style={{ fontSize: '3rem', margin: 0 }}>{report.compliance_score}%</h2>
        </div>
        <p style={{ fontSize: '1.125rem', color: 'var(--cds-text-secondary)' }}>Overall Compliance Score</p>
        <div style={{ marginTop: '1rem' }}>
          <ProgressBar
            value={report.compliance_score}
            label={`${report.compliance_score}% Compliant`}
            status={report.compliance_score >= 95 ? 'finished' : 'active'}
          />
        </div>
      </Tile>
    );
  };

  // Render metrics grid
  const renderMetrics = (metrics: ComplianceMetrics) => {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem',
        }}
      >
        {Object.entries(metrics).map(([key, value]) => (
          <Tile key={key} style={{ padding: '1.5rem' }}>
            <p style={{ color: 'var(--cds-text-secondary)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
              {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </p>
            <p style={{ fontSize: '1.75rem', fontWeight: 600, margin: 0 }}>
              {typeof value === 'number' ? (value % 1 === 0 ? value.toLocaleString() : value.toFixed(1)) : value}
            </p>
          </Tile>
        ))}
      </div>
    );
  };

  // Render violations table
  const renderViolationsTable = (violations: any[]) => {
    if (!violations || violations.length === 0) {
      return (
        <InlineNotification
          kind="success"
          title="No Violations"
          subtitle="No access violations found in the selected period"
          lowContrast
        />
      );
    }

    const headers = [
      { key: 'user_email', header: 'User' },
      { key: 'user_role', header: 'Role' },
      { key: 'resource_type', header: 'Resource' },
      { key: 'action_attempted', header: 'Action' },
      { key: 'timestamp', header: 'Timestamp' },
    ];

    const rows = violations.slice(0, 10).map((v, index) => ({
      id: `${index}`,
      user_email: v.user_email,
      user_role: v.user_role,
      resource_type: v.resource_type,
      action_attempted: v.action_attempted,
      timestamp: new Date(v.timestamp).toLocaleString(),
    }));

    return (
      <DataTable rows={rows} headers={headers} size="sm">
        {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
          <TableContainer
            title="Access Violations"
            description={violations.length > 10 ? `Showing 10 of ${violations.length} violations` : ''}
          >
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {headers.map(header => (
                    <TableHeader {...getHeaderProps({ header })} key={header.key}>
                      {header.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => (
                  <TableRow {...getRowProps({ row })} key={row.id}>
                    {row.cells.map(cell => (
                      <TableCell key={cell.id}>{cell.value}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataTable>
    );
  };

  // Render recommendations
  const renderRecommendations = (recommendations: string[]) => {
    return (
      <Tile
        style={{
          padding: '1.5rem',
          marginTop: '2rem',
          backgroundColor: 'var(--cds-layer-accent-01)',
          borderLeft: '4px solid var(--cds-border-interactive)',
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Recommendations</h3>
        <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
          {recommendations.map((rec, index) => (
            <li key={index} style={{ marginBottom: '0.5rem' }}>
              {rec}
            </li>
          ))}
        </ul>
      </Tile>
    );
  };

  return (
    <Grid fullWidth className="compliance-reports-page">
      {/* Header */}
      <Column lg={16}>
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Compliance Reports</h1>
          <p style={{ color: 'var(--cds-text-secondary)', fontSize: '0.875rem' }}>
            Generate FERPA, COPPA, and GDPR compliance reports for regulatory audits
          </p>
        </div>
      </Column>

      {/* Date Range Selector */}
      <Column lg={16}>
        <Tile style={{ padding: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <DatePicker datePickerType="single" value={startDate} onChange={(dates: Date[]) => setStartDate(dates[0])}>
              <DatePickerInput id="start-date" labelText="Start Date" placeholder="mm/dd/yyyy" />
            </DatePicker>
            <DatePicker datePickerType="single" value={endDate} onChange={(dates: Date[]) => setEndDate(dates[0])}>
              <DatePickerInput id="end-date" labelText="End Date" placeholder="mm/dd/yyyy" />
            </DatePicker>
            <Button
              kind="primary"
              onClick={() => {
                if (selectedTab === 0) fetchFerpaReport();
                else if (selectedTab === 1) fetchCoppaReport();
                else fetchGdprReport();
              }}
              disabled={loading}
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>
          </div>
        </Tile>
      </Column>

      {/* Notifications */}
      {error && (
        <Column lg={16}>
          <InlineNotification
            kind="error"
            title="Error"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
            style={{ marginBottom: '1rem' }}
          />
        </Column>
      )}

      {success && (
        <Column lg={16}>
          <InlineNotification
            kind="success"
            title="Success"
            subtitle={success}
            onCloseButtonClick={() => setSuccess(null)}
            style={{ marginBottom: '1rem' }}
          />
        </Column>
      )}

      {/* Compliance Framework Switcher */}
      <Column lg={16}>
        <div style={{ marginBottom: '2rem' }}>
          <ContentSwitcher
            selectedIndex={selectedTab}
            onChange={({ index }) => setSelectedTab(index as number)}
            size="lg"
          >
            <Switch name="ferpa" text="FERPA" />
            <Switch name="coppa" text="COPPA" />
            <Switch name="gdpr" text="GDPR" />
          </ContentSwitcher>
        </div>

        <div>
          {/* FERPA Panel */}
          {selectedTab === 0 && (
            <div>
              {loading ? (
                <Loading description="Loading FERPA report..." withOverlay={false} />
              ) : ferpaReport ? (
                <>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1rem',
                    }}
                  >
                    <h2>FERPA Compliance Report</h2>
                    <Button
                      kind="tertiary"
                      renderIcon={DocumentPdf}
                      onClick={() => exportToPdf('FERPA')}
                      disabled={exporting}
                    >
                      {exporting ? 'Exporting...' : 'Export to PDF'}
                    </Button>
                  </div>
                  {renderComplianceScore(ferpaReport)}
                  {renderMetrics(ferpaReport.metrics)}
                  {ferpaReport.violations && renderViolationsTable(ferpaReport.violations)}
                  {renderRecommendations(ferpaReport.recommendations)}
                </>
              ) : (
                <p>Select a date range and click "Generate Report" to view FERPA compliance data</p>
              )}
            </div>
          )}

          {/* COPPA Panel */}
          {selectedTab === 1 && (
            <div>
              {loading ? (
                <Loading description="Loading COPPA report..." withOverlay={false} />
              ) : coppaReport ? (
                <>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1rem',
                    }}
                  >
                    <h2>COPPA Compliance Report</h2>
                    <Button
                      kind="tertiary"
                      renderIcon={DocumentPdf}
                      onClick={() => exportToPdf('COPPA')}
                      disabled={exporting}
                    >
                      {exporting ? 'Exporting...' : 'Export to PDF'}
                    </Button>
                  </div>
                  {renderComplianceScore(coppaReport)}
                  {renderMetrics(coppaReport.metrics)}
                  {coppaReport.non_compliant_users && coppaReport.non_compliant_users.length > 0 && (
                    <Tile style={{ padding: '1.5rem', marginBottom: '2rem' }}>
                      <h3>Non-Compliant Users</h3>
                      <p style={{ color: 'var(--cds-text-secondary)' }}>
                        {coppaReport.non_compliant_users.length} children without proper parental consent
                      </p>
                    </Tile>
                  )}
                  {renderRecommendations(coppaReport.recommendations)}
                </>
              ) : (
                <p>Select a date range and click "Generate Report" to view COPPA compliance data</p>
              )}
            </div>
          )}

          {/* GDPR Panel */}
          {selectedTab === 2 && (
            <div>
              {loading ? (
                <Loading description="Loading GDPR report..." withOverlay={false} />
              ) : gdprReport ? (
                <>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1rem',
                    }}
                  >
                    <h2>GDPR Compliance Report</h2>
                    <Button
                      kind="tertiary"
                      renderIcon={DocumentPdf}
                      onClick={() => exportToPdf('GDPR')}
                      disabled={exporting}
                    >
                      {exporting ? 'Exporting...' : 'Export to PDF'}
                    </Button>
                  </div>
                  {renderComplianceScore(gdprReport)}
                  {renderMetrics(gdprReport.metrics)}
                  {gdprReport.data_subject_rights && (
                    <Tile style={{ padding: '1.5rem', marginBottom: '2rem' }}>
                      <h3>Data Subject Rights Requests</h3>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                          gap: '1rem',
                          marginTop: '1rem',
                        }}
                      >
                        <div>
                          <p style={{ color: 'var(--cds-text-secondary)', fontSize: '0.875rem' }}>Export Requests</p>
                          <p style={{ fontSize: '1.5rem', fontWeight: 600 }}>
                            {gdprReport.data_subject_rights.export_requests}
                          </p>
                        </div>
                        <div>
                          <p style={{ color: 'var(--cds-text-secondary)', fontSize: '0.875rem' }}>Deletion Requests</p>
                          <p style={{ fontSize: '1.5rem', fontWeight: 600 }}>
                            {gdprReport.data_subject_rights.deletion_requests}
                          </p>
                        </div>
                      </div>
                    </Tile>
                  )}
                  {renderRecommendations(gdprReport.recommendations)}
                </>
              ) : (
                <p>Select a date range and click "Generate Report" to view GDPR compliance data</p>
              )}
            </div>
          )}
        </div>
      </Column>
    </Grid>
  );
};

export default ComplianceReportsPage;
